if (!customElements.get('yb-region-selector')) {
  const COUNTRY_LANGUAGE_MAP = {
    CN: 'zh-CN',
  };
  const LANGUAGE_LABELS = {
    en: 'English',
    zh: 'Simplified Chinese',
    'zh-CN': 'Simplified Chinese',
  };
  const BASE_SUPPORTED_OPTIONS = [
    {
      countryCode: 'US',
      countryName: 'United States',
      currencyCode: 'USD',
      flag: '🇺🇸',
      regionGroup: 'north-america',
    },
    {
      countryCode: 'DE',
      countryName: 'Germany',
      currencyCode: 'EUR',
      flag: '🇩🇪',
      regionGroup: 'europe',
    },
    {
      countryCode: 'GB',
      countryName: 'United Kingdom',
      currencyCode: 'GBP',
      flag: '🇬🇧',
      regionGroup: 'europe',
    },
    {
      countryCode: 'FR',
      countryName: 'France',
      currencyCode: 'EUR',
      flag: '🇫🇷',
      regionGroup: 'europe',
    },
    {
      countryCode: 'CA',
      countryName: 'Canada',
      currencyCode: 'CAD',
      flag: '🇨🇦',
      regionGroup: 'north-america',
    },
    {
      countryCode: 'IT',
      countryName: 'Italy',
      currencyCode: 'EUR',
      flag: '🇮🇹',
      regionGroup: 'europe',
    },
    {
      countryCode: 'CN',
      countryName: 'China',
      currencyCode: 'USD',
      flag: '🇨🇳',
      regionGroup: 'asia',
    },
  ];
  const SUPPORTED_REGION_GROUPS = [
    {
      id: 'north-america',
      label: 'North America',
    },
    {
      id: 'europe',
      label: 'Europe',
    },
    {
      id: 'asia',
      label: 'Asia',
    },
  ];
  const configuredLanguageCodes = Array.isArray(window.YB_AUTO_LOCALIZATION?.availableLanguages)
    ? window.YB_AUTO_LOCALIZATION.availableLanguages.map((languageCode) => String(languageCode)).filter(Boolean)
    : ['en'];
  const availableLanguageLookup = new Map(
    configuredLanguageCodes.map((languageCode) => [languageCode.toLowerCase(), languageCode])
  );

  const getAvailableLanguageCode = (preferredLanguage = 'en') => {
    const normalizedPreferredLanguage = String(preferredLanguage || 'en').toLowerCase();

    if (availableLanguageLookup.has(normalizedPreferredLanguage)) {
      return availableLanguageLookup.get(normalizedPreferredLanguage);
    }

    if (normalizedPreferredLanguage.includes('-')) {
      const baseLanguage = normalizedPreferredLanguage.split('-')[0];
      if (availableLanguageLookup.has(baseLanguage)) {
        return availableLanguageLookup.get(baseLanguage);
      }
    }

    return availableLanguageLookup.get('en') || 'en';
  };

  const getLanguageLabel = (languageCode) => {
    const normalizedLanguage = String(languageCode || 'en');
    return LANGUAGE_LABELS[normalizedLanguage] || LANGUAGE_LABELS[normalizedLanguage.split('-')[0]] || 'English';
  };

  const getLanguageForCountry = (countryCode) => {
    const requestedLanguage = COUNTRY_LANGUAGE_MAP[countryCode] || 'en';
    const languageCode = getAvailableLanguageCode(requestedLanguage);

    return {
      languageCode,
      languageLabel: getLanguageLabel(languageCode),
    };
  };

  const SUPPORTED_OPTIONS = BASE_SUPPORTED_OPTIONS.map((option) => ({
    ...option,
    ...getLanguageForCountry(option.countryCode),
  }));

  customElements.define(
    'yb-region-selector',
    class YeahboxRegionSelector extends HTMLElement {
      constructor() {
        super();
        this.trigger = this.querySelector('.yb-region-selector__trigger');
        this.dialog = this.querySelector('.yb-region-selector__dialog');
        this.overlay = this.querySelector('.yb-region-selector__overlay');
        this.saveButton = this.querySelector('.yb-region-selector__save');
        this.optionsContainer = this.querySelector('[data-region-options]');
        this.countryValue = this.querySelector('[data-region-country]');
        this.currencyValue = this.querySelector('[data-region-currency]');
        this.languageValue = this.querySelector('[data-region-language]');
        this.summaryFull = this.querySelector('[data-region-summary-full]');
        this.summaryLocale = this.querySelector('[data-region-summary-locale]');
        this.summaryFlag = this.querySelector('[data-region-summary-flag]');
        this.activeTrigger = this.trigger;
        this.isSubmitting = false;
        this.hasRenderedOptions = false;
        this.currentCountry = String(this.dataset.currentCountry || '').toUpperCase();
        this.currentCountryName = this.dataset.currentCountryName || this.currentCountry;
        this.currentCurrency = String(this.dataset.currentCurrency || '').toUpperCase();
        this.currentLanguage = this.dataset.currentLanguage || '';
        this.currentLanguageLabel = this.dataset.currentLanguageLabel || this.currentLanguage || 'English';
        this.defaultCountry = String(this.dataset.defaultCountry || 'US').toUpperCase();
        this.selectedOption = this.getInitialOption();
        this.onExternalTriggerClick = this.onExternalTriggerClick.bind(this);
        this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
      }

      connectedCallback() {
        this.trigger?.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle(event.currentTarget);
        });
        this.saveButton?.addEventListener('click', () => this.submitLocalizationChange());
        this.querySelectorAll('[data-region-close]').forEach((button) => {
          button.addEventListener('click', () => this.close());
        });
        document.addEventListener('click', this.onExternalTriggerClick);
        document.addEventListener('keydown', this.onDocumentKeyDown);
        this.updateSelectedState();
        this.updateSummary();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onExternalTriggerClick);
        document.removeEventListener('keydown', this.onDocumentKeyDown);
      }

      getStorageValue(key) {
        try {
          return window.localStorage.getItem(key);
        } catch (error) {
          return null;
        }
      }

      setStorageValue(key, value) {
        try {
          window.localStorage.setItem(key, value);
        } catch (error) {
          // Manual form submission still works if storage is blocked.
        }
      }

      getCookieValue(key) {
        const cookies = document.cookie ? document.cookie.split(';') : [];
        const prefix = `${key}=`;
        const cookie = cookies.map((item) => item.trim()).find((item) => item.startsWith(prefix));
        return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
      }

      setCookieValue(key, value) {
        document.cookie = `${key}=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax`;
      }

      hasManualChoice() {
        return (
          this.getStorageValue('yb-localization-manual-choice') === 'true' ||
          this.getCookieValue('yb-localization-manual-choice') === 'true'
        );
      }

      getOption(countryCode) {
        const normalized = String(countryCode || '').toUpperCase();
        return SUPPORTED_OPTIONS.find((option) => option.countryCode === normalized);
      }

      getInitialOption() {
        const savedCountry = this.hasManualChoice()
          ? this.getStorageValue('yb-localization-country') || this.getCookieValue('yb-localization-country')
          : this.currentCountry;

        return this.getOption(savedCountry) || this.getOption(this.defaultCountry) || SUPPORTED_OPTIONS[0];
      }

      getCurrentMarketSummary() {
        const option = this.getOption(this.currentCountry) || this.getOption(this.defaultCountry) || SUPPORTED_OPTIONS[0];

        return [option.countryName, option.languageLabel, option.currencyCode].filter(Boolean).join(' · ');
      }

      getCurrentMarketLocaleSummary() {
        const option = this.getOption(this.currentCountry) || this.getOption(this.defaultCountry) || SUPPORTED_OPTIONS[0];

        return [option.countryName, option.languageLabel].filter(Boolean).join(' / ');
      }

      getCurrentMarketFlag() {
        const option = this.getOption(this.currentCountry) || this.getOption(this.defaultCountry) || SUPPORTED_OPTIONS[0];

        return option.flag || '';
      }

      updateSummary() {
        if (this.summaryFull) this.summaryFull.textContent = this.getCurrentMarketSummary();
        if (this.summaryLocale) this.summaryLocale.textContent = this.getCurrentMarketLocaleSummary();
        if (this.summaryFlag) this.summaryFlag.textContent = this.getCurrentMarketFlag();
      }

      onExternalTriggerClick(event) {
        const trigger = event.target.closest('[data-yb-region-open]');
        if (!trigger) return;
        event.preventDefault();
        this.toggle(trigger);
      }

      onDocumentKeyDown(event) {
        if (event.key === 'Escape' && this.isOpen()) {
          event.preventDefault();
          this.close();
        }
      }

      isOpen() {
        return Boolean(this.dialog && !this.dialog.hidden);
      }

      toggle(triggeredBy = this.trigger) {
        if (this.isOpen()) {
          this.close();
          return;
        }

        this.open(triggeredBy);
      }

      renderOptions() {
        if (!this.optionsContainer || this.hasRenderedOptions) return;

        const fragment = document.createDocumentFragment();
        SUPPORTED_REGION_GROUPS.forEach((group) => {
          const groupOptions = SUPPORTED_OPTIONS.filter((option) => option.regionGroup === group.id);
          if (!groupOptions.length) return;

          const section = document.createElement('section');
          section.className = 'yb-region-selector__option-group';
          section.setAttribute('aria-label', group.label);

          const heading = document.createElement('h4');
          heading.className = 'yb-region-selector__option-group-title';
          heading.textContent = group.label;
          section.appendChild(heading);

          groupOptions.forEach((option) => {
          const item = document.createElement('button');
          item.type = 'button';
          item.className = 'yb-region-selector__option';
          item.dataset.countryCode = option.countryCode;
          item.setAttribute('aria-pressed', option.countryCode === this.selectedOption.countryCode ? 'true' : 'false');

          const main = document.createElement('span');
          main.className = 'yb-region-selector__option-main';
          const flag = document.createElement('span');
          flag.className = 'yb-region-selector__option-flag';
          flag.textContent = option.flag;
          const countryName = document.createElement('span');
          countryName.className = 'yb-region-selector__option-country';
          countryName.textContent = option.countryName;
          const languageName = document.createElement('span');
          languageName.className = 'yb-region-selector__option-language';
          languageName.textContent = option.languageLabel;
          main.append(flag, countryName, languageName);

          const secondary = document.createElement('span');
          secondary.className = 'yb-region-selector__option-sub';
          secondary.textContent = option.currencyCode;
          item.append(main, secondary);

          item.addEventListener('click', () => {
            this.selectedOption = option;
            this.updateSelectedState();
          });
          section.appendChild(item);
        });

          fragment.appendChild(section);
        });

        this.optionsContainer.appendChild(fragment);
        this.hasRenderedOptions = true;
      }

      updateSelectedState() {
        const option = this.selectedOption || SUPPORTED_OPTIONS[0];
        if (this.countryValue) this.countryValue.textContent = option.countryName;
        if (this.currencyValue) this.currencyValue.textContent = option.currencyCode;
        if (this.languageValue) this.languageValue.textContent = option.languageLabel;

        this.querySelectorAll('[data-region-options] .yb-region-selector__option').forEach((item) => {
          item.setAttribute('aria-pressed', item.dataset.countryCode === option.countryCode ? 'true' : 'false');
        });
      }

      setExpandedState(isExpanded) {
        this.trigger?.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        document.querySelectorAll('[data-yb-region-open]').forEach((button) => {
          button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        });
      }

      open(triggeredBy = this.trigger) {
        this.activeTrigger = triggeredBy || this.trigger;
        this.renderOptions();
        this.updateSelectedState();
        this.dialog?.removeAttribute('hidden');
        this.dialog?.removeAttribute('aria-hidden');
        this.dialog?.removeAttribute('inert');
        this.overlay?.removeAttribute('hidden');
        this.overlay?.removeAttribute('aria-hidden');
        this.overlay?.removeAttribute('inert');
        this.setExpandedState(true);
        document.documentElement.classList.add('yb-region-selector-open');
        this.querySelector('.yb-region-selector__option[aria-pressed="true"]')?.focus();
      }

      close() {
        this.dialog?.setAttribute('hidden', '');
        this.dialog?.setAttribute('aria-hidden', 'true');
        this.dialog?.setAttribute('inert', '');
        this.overlay?.setAttribute('hidden', '');
        this.overlay?.setAttribute('aria-hidden', 'true');
        this.overlay?.setAttribute('inert', '');
        this.setExpandedState(false);
        document.documentElement.classList.remove('yb-region-selector-open');
        this.activeTrigger?.focus();
      }

      setManualPreference(option) {
        this.setStorageValue('yb-localization-manual-choice', 'true');
        this.setStorageValue('yb-localization-country', option.countryCode);
        this.setStorageValue('yb-localization-language', option.languageCode);
        this.setCookieValue('yb-localization-manual-choice', 'true');
        this.setCookieValue('yb-localization-country', option.countryCode);
        this.setCookieValue('yb-localization-language', option.languageCode);
      }

      submitLocalizationChange() {
        if (this.isSubmitting) return;
        const option = this.selectedOption || SUPPORTED_OPTIONS[0];

        this.isSubmitting = true;
        this.setManualPreference(option);

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = this.dataset.localizationPath || '/localization';
        form.acceptCharset = 'UTF-8';
        form.style.display = 'none';

        this.appendHiddenField(form, 'form_type', 'localization');
        this.appendHiddenField(form, 'utf8', '✓');
        this.appendHiddenField(form, '_method', 'put');
        this.appendHiddenField(form, 'country_code', option.countryCode);
        this.appendHiddenField(form, 'language_code', option.languageCode);
        this.appendHiddenField(form, 'return_to', `${window.location.pathname}${window.location.search}${window.location.hash}`);

        document.body.appendChild(form);
        form.submit();
      }

      appendHiddenField(form, name, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      }
    }
  );
}
